/**
 * "Chạy thật (demo)": chạy luồng đang ở canvas với dữ liệu thật — gửi tin Zalo
 * cá nhân cho một người nhận do người dùng chỉ định, gắn nhãn và ghi CDP thật.
 * Thời gian được nén (1 ngày = N giây) và từng bước hiện realtime qua Socket.IO.
 * Chỉ owner/admin dùng được; phải tick xác nhận trước khi gửi.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Play, Loader2, AlertTriangle, CheckCircle2, XCircle, Clock, SkipForward } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { api, apiError } from '@/lib/api-client'
import { getSocket } from '@/lib/socket'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { LINK_SAN_PHAM_MAC_DINH } from '@/lib/automation-v2/mau-workflow'
import type { FlowEdgeV2, FlowNodeV2 } from '@/lib/automation-v2/types'

export interface BuocChay {
  nodeId: string
  kind: string
  label: string
  ngay: number
  luc: string
  trangThai: 'ok' | 'fail' | 'skip' | 'wait'
  tomTat: string
}
interface KetQuaBatDau { runId: string; hoiThoaiId: string; tenKhach: string; nick: string; soTinToiDa: number; nen: number }

const BIEU_TUONG: Record<BuocChay['trangThai'], typeof CheckCircle2> = { ok: CheckCircle2, fail: XCircle, wait: Clock, skip: SkipForward }
const MAU_TRANG_THAI: Record<BuocChay['trangThai'], string> = { ok: 'text-success', fail: 'text-destructive', wait: 'text-warning', skip: 'text-muted-foreground' }

export function ChayThatDialog({
  open, onClose, layLuong, tenLuong, onBuoc, onXong,
}: {
  open: boolean
  onClose: () => void
  layLuong: () => { name?: string; nodes: FlowNodeV2[]; edges: FlowEdgeV2[] }
  tenLuong: string
  /** Tô màu khối trên canvas theo từng bước. */
  onBuoc?: (b: BuocChay) => void
  onXong?: () => void
}) {
  const { data: nicks } = useZaloAccounts('personal', true)
  const [sdt, setSdt] = useState('')
  const [nickId, setNickId] = useState('')
  const [nen, setNen] = useState('10')
  const [link, setLink] = useState(LINK_SAN_PHAM_MAC_DINH)
  const [tongDon, setTongDon] = useState('640000')
  const [tenSp, setTenSp] = useState('Amoxicillin 500mg (hộp 100 viên)')
  const [nhomHang, setNhomHang] = useState('Kháng sinh')
  const [xacNhan, setXacNhan] = useState(false)
  const [dangChay, setDangChay] = useState<KetQuaBatDau | null>(null)
  const [buoc, setBuoc] = useState<BuocChay[]>([])
  const [xong, setXong] = useState<{ soBuoc: number; ngay: number; giay: number } | null>(null)
  const cuonRef = useRef<HTMLDivElement>(null)

  const nickSong = (nicks ?? []).filter((n) => n.status === 'connected')
  useEffect(() => { if (!nickId && nickSong[0]) setNickId(nickSong[0].id) }, [nickSong, nickId])
  useEffect(() => { cuonRef.current?.scrollTo({ top: cuonRef.current.scrollHeight }) }, [buoc])

  // Nhận từng bước realtime của lượt chạy đang mở
  useEffect(() => {
    if (!dangChay) return
    const socket = getSocket()
    const onBuocSk = (p: { runId: string; buoc: BuocChay }) => {
      if (p.runId !== dangChay.runId) return
      setBuoc((b) => [...b, p.buoc])
      onBuoc?.(p.buoc)
    }
    const onXongSk = (p: { runId: string; soBuoc: number; ngay: number; giay: number }) => {
      if (p.runId !== dangChay.runId) return
      setXong({ soBuoc: p.soBuoc, ngay: p.ngay, giay: p.giay })
      onXong?.()
      toast.success(`Chạy xong: ${p.soBuoc} bước · ${p.ngay} ngày mô phỏng trong ${p.giay}s`)
    }
    socket.on('automation-v2:buoc', onBuocSk)
    socket.on('automation-v2:xong', onXongSk)
    return () => { socket.off('automation-v2:buoc', onBuocSk); socket.off('automation-v2:xong', onXongSk) }
  }, [dangChay, onBuoc, onXong])

  const batDau = useMutation({
    mutationFn: async (): Promise<KetQuaBatDau> => {
      const { data } = await api.post<KetQuaBatDau>('/automation-v2/demo/chay-that', {
        luong: layLuong(),
        sdt: sdt.trim(),
        nickId: nickId || null,
        nen: Number(nen) || 10,
        linkSanPham: link.trim() || null,
        donMau: { total: Number(tongDon) || 640000, itemName: tenSp.trim() || undefined, itemCategory: nhomHang.trim() || undefined },
        xacNhan: true,
      })
      return data
    },
    onSuccess: (kq) => {
      setDangChay(kq); setBuoc([]); setXong(null)
      toast.success(`Bắt đầu gửi cho ${kq.tenKhach} qua nick ${kq.nick}`)
    },
    onError: (err) => toast.error(apiError(err)),
  })

  const dong = () => { onClose(); if (xong) { setDangChay(null); setBuoc([]); setXong(null); setXacNhan(false) } }
  const tongNgay = 30

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dong()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Chạy thật luồng “{tenLuong}”<Badge variant="warning">GỬI TIN THẬT</Badge></DialogTitle>
          <DialogDescription>
            Luồng chạy trên dữ liệu thật: gửi tin Zalo cá nhân cho người nhận bên dưới, gắn nhãn và ghi thuộc tính CDP.
            Tạo việc / voucher / Sheet chỉ ghi nhật ký. Thời gian được nén để xem hết chuỗi trong ít phút.
          </DialogDescription>
        </DialogHeader>

        {!dangChay ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Số Zalo người nhận</Label>
                <Input value={sdt} onChange={(e) => setSdt(e.target.value)} placeholder="0349487999" className="h-9" />
                <p className="mt-1 text-[11px] text-muted-foreground">Nick gửi phải đã kết bạn hoặc từng nhắn với số này.</p>
              </div>
              <div>
                <Label className="text-xs">Nick Zalo gửi</Label>
                <Select value={nickId} onValueChange={setNickId}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Chọn nick đang kết nối" /></SelectTrigger>
                  <SelectContent>
                    {nickSong.map((n) => <SelectItem key={n.id} value={n.id}>{n.displayName ?? n.phone ?? n.id.slice(0, 8)} · {n.phone ?? ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                {nickSong.length === 0 && <p className="mt-1 text-[11px] text-destructive">Chưa có nick Zalo nào đang kết nối.</p>}
              </div>
              <div>
                <Label className="text-xs">Nén thời gian (1 ngày = ? giây)</Label>
                <Select value={nen} onValueChange={setNen}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 giây — {tongNgay} ngày ≈ 2,5 phút</SelectItem>
                    <SelectItem value="10">10 giây — {tongNgay} ngày ≈ 5 phút</SelectItem>
                    <SelectItem value="20">20 giây — {tongNgay} ngày ≈ 10 phút</SelectItem>
                    <SelectItem value="30">30 giây — {tongNgay} ngày ≈ 15 phút</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tổng tiền đơn mẫu</Label>
                <Input value={tongDon} onChange={(e) => setTongDon(e.target.value)} className="h-9" placeholder="640000" />
                <p className="mt-1 text-[11px] text-muted-foreground">≥ 1 triệu sẽ đi nhánh “tạo việc cho dược sĩ”.</p>
              </div>
              <div>
                <Label className="text-xs">Sản phẩm khách đã mua</Label>
                <Input value={tenSp} onChange={(e) => setTenSp(e.target.value)} className="h-9" placeholder="Amoxicillin 500mg" />
                <p className="mt-1 text-[11px] text-muted-foreground">Tên này được nhắc thẳng trong tin nhắn.</p>
              </div>
              <div>
                <Label className="text-xs">Nhóm hàng</Label>
                <Select value={nhomHang} onValueChange={setNhomHang}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kháng sinh">Kháng sinh — gợi ý men vi sinh</SelectItem>
                    <SelectItem value="Trà xanh">Trà xanh — mời duy trì liệu trình</SelectItem>
                    <SelectItem value="Thực phẩm chức năng">Thực phẩm chức năng</SelectItem>
                    <SelectItem value="Dược liệu">Dược liệu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Link mua hàng cho biến {'{{link.sanPham}}'}</Label>
              <Input value={link} onChange={(e) => setLink(e.target.value)} className="h-9 font-mono text-[11px]" />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-2.5 text-[12px]">
              <Switch checked={xacNhan} onCheckedChange={setXacNhan} />
              <span>Tôi xác nhận tin nhắn sẽ được <b>gửi thật</b> tới số Zalo ở trên bằng nick đã chọn.</span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={dong}>Huỷ</Button>
              <Button disabled={!xacNhan || !sdt.trim() || !nickId || batDau.isPending} onClick={() => batDau.mutate()}>
                {batDau.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Bắt đầu gửi
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-[12px]">
              <Badge variant="outline">{dangChay.tenKhach}</Badge>
              <span className="text-muted-foreground">qua nick {dangChay.nick} · 1 ngày = {dangChay.nen}s</span>
              {!xong && <span className="ml-auto flex items-center gap-1 text-primary"><Loader2 className="h-3.5 w-3.5 animate-spin" /> đang chạy…</span>}
              {xong && <span className="ml-auto text-success">Xong · {xong.soBuoc} bước · {xong.ngay} ngày mô phỏng trong {xong.giay}s</span>}
            </div>
            <div ref={cuonRef} className="max-h-[50vh] space-y-1 overflow-y-auto rounded-md border p-2">
              {buoc.length === 0 && <p className="p-2 text-[12px] text-muted-foreground">Đang khởi động…</p>}
              {buoc.map((b, i) => { const I = BIEU_TUONG[b.trangThai]; return (
                <div key={i} className="flex items-start gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-accent/40">
                  <I className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', MAU_TRANG_THAI[b.trangThai])} />
                  <span className="w-16 shrink-0 tabular-nums text-muted-foreground">ngày {b.ngay}</span>
                  <span className="min-w-0 flex-1"><b>{b.label}</b> — {b.tomTat}</span>
                </div>
              ) })}
            </div>
            {!xong && (
              <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" /> Đóng hộp thoại không dừng lượt chạy — tin vẫn tiếp tục gửi theo lịch nén.
              </p>
            )}
            <div className="flex justify-end"><Button variant={xong ? 'default' : 'ghost'} onClick={dong}>{xong ? 'Đóng' : 'Ẩn'}</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
